import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, Navigate } from 'react-router-dom';
import { getPageBySlug, resolveCanonicalSlug } from '../lib/cms';
import Gallery from '../components/Gallery';
import ContactForm from '../components/ContactForm';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';

// Map page slugs to beautiful relevant header background images with SEO-friendly filenames
const HEADER_IMAGES = {
  'index': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-lastbil-flaade.jpg',
  'vognmand': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/vognmandskoersel-viborg-skals.jpg',
  'grus-og-sten': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/sand-og-grus-materialer-skals.jpeg',
  'containerudlejning': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/containerudlejning-og-containerservice-viborg.jpg',
  'containerkoersel': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/containerudlejning-og-containerservice-viborg.jpg',
  'containerkørsel': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/containerudlejning-og-containerservice-viborg.jpg',
  'arbejde-med-gummiged': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/krankoersel-og-gummiged-viborg.jpg',
  'krankoersel': 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-kranbil-viborg.jpeg',
  'haveflis': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-lastbil-flaade.jpg',
  'udlejning-af-maskiner': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/cementblander-udlejning-skals.jpg',
  'profil': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/vognmandskoersel-midtjylland-skals.jpg',
  'vores-lastbiler': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/man-lastbil-skals-vognmandsforretning.jpg',
  'kontakt': 'https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-lastbil-flaade.jpg'
};

const DEFAULT_META_DESC = "Skals Vognmandsforretning tilbyder professionel vognmandskørsel fra Skals med fokus på stabil service, erfaring og pålidelige løsninger.";

export default function PageTemplate() {
  const { slug } = useParams();
  const location = useLocation();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const pageSlug = resolveCanonicalSlug(slug || 'index');

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      const pageData = await getPageBySlug(pageSlug);
      setPage(pageData);
      setLoading(false);
    }
    loadPage();
  }, [pageSlug]);

  // Update document metadata dynamically (Title, Description, OpenGraph, Twitter, Canonical)
  useEffect(() => {
    if (page) {
      const pageTitle = page.title || "Skals Vognmandsforretning";
      const desc = page.meta_description || DEFAULT_META_DESC;
      const imageUrl = HEADER_IMAGES[pageSlug] || HEADER_IMAGES['index'];
      const pageUrl = window.location.href;

      // Title
      document.title = pageTitle;

      // Helper to update or create meta element
      const setMeta = (selector, attributeName, attributeVal, content) => {
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attributeName, attributeVal);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      // Standard Description
      setMeta('meta[name="description"]', 'name', 'description', desc);

      // Open Graph Tags
      setMeta('meta[property="og:title"]', 'property', 'og:title', pageTitle);
      setMeta('meta[property="og:description"]', 'property', 'og:description', desc);
      setMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
      setMeta('meta[property="og:url"]', 'property', 'og:url', pageUrl);
      setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
      setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Skals Vognmandsforretning');

      // Twitter Card Tags
      setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', pageTitle);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', desc);
      setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);

      // Canonical Link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', pageUrl);
    }
  }, [page, pageSlug]);

  // Auto-play hero slider for home page
  useEffect(() => {
    if (pageSlug !== 'index') return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === 2 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [pageSlug]);

  if (slug === 'containerudlejning' || slug === 'containerudlejning-i-skals-viborg-skive-omegn-leje-af-container') {
    return <Navigate to="/containerkoersel" replace />;
  }

  const handleCallbackSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const websiteUrl = formData.get('website_url');

    try {
      const response = await fetch('https://api.dev.auxo.dkwebform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.get('name'),
          telephone: formData.get('telephone'),
          website_url: websiteUrl || ''
        })
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      event.currentTarget.reset();
    } catch (error) {
      console.error('Callback form submit error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-black text-brand-dark mb-4">404 - Siden blev ikke fundet</h1>
        <p className="text-text-secondary mb-8">Beklager, men den side du leder efter findes ikke.</p>
        <Link to="/" className="bg-brand-red hover:bg-brand-red-hover text-white font-bold px-6 py-3 rounded-none shadow-sm transition-colors">
          Gå til forsiden
        </Link>
      </div>
    );
  }

  // Render Home Page (index)
  if (pageSlug === 'index') {
    const slides = [
      {
        title: "Skals Vognmandsforretning – en transportløsning til dig!",
        text: "Uanset som du er førstegangskunde hos os, eller kender til vores service og har været i kontakt med os flere gange bliver alle vores kunder behandlet godt.",
        ctaText: "Kontakt os i dag",
        ctaLink: "/kontakt",
        image: HEADER_IMAGES['index']
      },
      {
        title: "Transport af sand og grus m.m. – en transportløsning til dig!",
        text: "Vi sælger flere forskellige typer sand, grus og sten hos Skals Vognmandsforretning. Har du f.eks. brug for granit til din grund, kan vi skaffe det til dig.",
        ctaText: "Kontakt os i dag",
        ctaLink: "/kontakt",
        secondaryText: "Læs mere",
        secondaryLink: "/grus-og-sten",
        image: HEADER_IMAGES['grus-og-sten']
      },
      {
        title: "Vores containerservice – udlejning efter dine behov",
        text: "Vi tilbyder både åbne og lukkede containere, så du kan få en container, der passer til dit formål. Du kan både som privatperson og i erhvervsmæssige sammenhænge kontakte os.",
        ctaText: "Kontakt os i dag",
        ctaLink: "/kontakt",
        secondaryText: "Læs mere",
        secondaryLink: "/containerkoersel",
        image: HEADER_IMAGES['containerkoersel']
      }
    ];

    return (
      <div className="animate-in fade-in duration-300">
        {/* Hero Slider */}
        <section className="relative h-[550px] sm:h-[600px] bg-brand-dark overflow-hidden">
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              {/* Background Image with Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/95 via-brand-dark/75 to-transparent z-10" />
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-center scale-105"
              />

              {/* Slide Content */}
              <div className="absolute inset-0 z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                <div className="max-w-2xl text-white text-center mx-auto">
                  <span className="inline-block bg-brand-red text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-none mb-4">
                    Skals Vognmandsforretning
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-4 text-white">
                    {slide.title}
                  </h1>
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-8">
                    {slide.text}
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <Link
                      to={slide.ctaLink}
                      className="px-8 py-3 bg-brand-red text-white text-sm font-bold uppercase tracking-wider rounded-none hover:bg-brand-red-hover transition-colors duration-200"
                    >
                      {slide.ctaText}
                    </Link>
                    {slide.secondaryText && (
                      <Link
                        to={slide.secondaryLink}
                        className="px-8 py-3 bg-white text-brand-dark text-sm font-bold uppercase tracking-wider rounded-none hover:bg-slate-100 transition-colors duration-200"
                      >
                        {slide.secondaryText}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Slider Indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2.5">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-2 transition-all duration-300 ${
                  idx === currentSlide ? 'w-8 bg-brand-red' : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Gå til slide ${idx + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Main Intro & Service Grid Section (Split-Screen) */}
        <section className="bg-white text-neutral-950">
          <div className="mx-auto grid max-w-[1600px] lg:grid-cols-2">
            {/* Left Column: Text Content */}
            <div className="px-6 py-10 md:px-12 lg:px-16 xl:px-20 lg:py-16 flex flex-col justify-center">
              <div className="max-w-[42rem]">
                <div className="inline-flex items-center gap-2 bg-brand-red/10 text-brand-red px-3 py-1 text-xs font-bold uppercase tracking-wider mb-4">
                  <i className="fa-solid fa-truck" aria-hidden="true"></i>
                  <span>Skals Vognmandsforretning</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-[1.15] tracking-[-0.02em] text-neutral-900">
                  Vognmand i Skals med løsninger til din opgave
                </h2>
                <div className="mt-6 space-y-4 text-base sm:text-lg leading-relaxed text-neutral-600">
                  <p>
                    Har du brug for en vognmand, der kan tage hånd om både transport, materialer, containere og løft? Hos Skals Vognmandsforretning hjælper vi private, landbrug og erhverv med praktiske løsninger, der bliver planlagt efter opgaven. Vi kører fra Skals og løser opgaver i blandt andet Viborg, Skive og Hobro.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-neutral-100 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm font-semibold text-neutral-700">
                  <span className="flex items-center gap-1.5"><i className="fa-solid fa-location-dot text-brand-red" aria-hidden="true"></i> Skals</span>
                  <span className="flex items-center gap-1.5"><i className="fa-solid fa-location-dot text-brand-red" aria-hidden="true"></i> Viborg</span>
                  <span className="flex items-center gap-1.5"><i className="fa-solid fa-location-dot text-brand-red" aria-hidden="true"></i> Skive</span>
                  <span className="flex items-center gap-1.5"><i className="fa-solid fa-location-dot text-brand-red" aria-hidden="true"></i> Hobro</span>
                </div>
              </div>
            </div>

            {/* Right Column: Image Showcase Grid (2x2 Grid) */}
            <div className="grid aspect-[4/5] w-full grid-cols-1 grid-rows-4 gap-0 sm:grid-cols-2 sm:grid-rows-2 sm:aspect-[5/6] md:aspect-[6/7] lg:aspect-auto lg:h-full">
              <Link to="/krankoersel" className="group relative h-full overflow-hidden bg-zinc-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-kranbil-viborg.jpeg"
                  alt="Krankørsel og løfteopgaver med kranbil i Viborg og Skals"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/35" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <h3 className="text-center text-2xl font-medium text-white sm:text-3xl">
                    Kranarbejde
                  </h3>
                </div>
              </Link>

              <Link to="/containerkoersel" className="group relative h-full overflow-hidden bg-zinc-900">
                <img
                  src="https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/containerudlejning-og-containerservice-viborg.jpg"
                  alt="Containerservice og containerudlejning i Skals, Viborg og Skive"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/35" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <h3 className="text-center text-2xl font-medium text-white sm:text-3xl">
                    Containerservice
                  </h3>
                </div>
              </Link>

              <Link to="/vognmand" className="group relative h-full overflow-hidden bg-zinc-900">
                <img
                  src="https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/skals-vognmandsforretning-lastbil-flaade.jpg"
                  alt="Vognmandskørsel og entreprenørkørsel med lastbil i Viborg"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/35" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <h3 className="text-center text-2xl font-medium text-white sm:text-3xl">
                    Vognmandskørsel
                  </h3>
                </div>
              </Link>

              <Link to="/grus-og-sten" className="group relative h-full overflow-hidden bg-zinc-900">
                <img
                  src="https://assets.auxodev.dk/assets/3c12e69c-355b-4bf8-827c-6379a757f0e7/sand-og-grus-materialer-skals.jpeg"
                  alt="Levering af sand, grus, skærver og sten i Skals og Viborg"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/35" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <h3 className="text-center text-2xl font-medium text-white sm:text-3xl">
                    Sand og grus
                  </h3>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Structured SEO Services & Solutions Section */}
        <section className="py-16 lg:py-24 bg-slate-50 border-t border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="text-brand-red font-bold uppercase tracking-wider text-xs sm:text-sm">
                Vores ydelser & transportløsninger
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-neutral-900 mt-2">
                Effektiv vognmandskørsel tilpasset din opgave
              </h2>
              <div className="w-16 h-1 bg-brand-red mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Card 1: Vognmandskørsel */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-truck-moving" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Få samlet din vognmandskørsel ét sted
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Vores vognmandskørsel omfatter blandt andet entreprenørkørsel, materialetransport samt levering og bortkørsel. Du får en løsning, hvor mængde, adgangsforhold, tidspunkt og placering bliver afklaret på forhånd.
                  </p>
                </div>
                <Link
                  to="/vognmand"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Læs om vognmandskørsel</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Card 2: Sand og grus */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-cubes" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Materialer leveret i den rette mængde
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Vi leverer sand, grus, sten og jord i mængder, der passer til dit behov. Det gælder alt fra stabilgrus og brolæggersand til perlegrus og granitskærver. Mindre mængder kan også afhentes med trailer fra Skals.
                  </p>
                </div>
                <Link
                  to="/grus-og-sten"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Læs om sand og grus</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Card 3: Containerkørsel */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-dumpster" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Containerkørsel der gør oprydningen lettere
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Med containerkørsel får du containeren leveret, placeret og hentet igen efter aftale. Vi tilbyder åbne og lukkede containere i forskellige størrelser til jord, haveaffald, murbrokker og byggeaffald.
                  </p>
                </div>
                <Link
                  to="/containerkoersel"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Læs om containerkørsel</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Card 4: Krankørsel */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-truck-ramp-box" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Krankørsel til tunge og besværlige løft
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Vores krankørsel er relevant, når materialer og emner ikke kan håndteres på almindelig vis. Med kranbil hjælper vi med byggeelementer, haveaffald, træer samt andre tunge løft med op til 29 tonmeter kran.
                  </p>
                </div>
                <Link
                  to="/krankoersel"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Læs om krankørsel</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Card 5: Maskinudlejning */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-trowel-bricks" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Udlejning af maskiner til dit projekt
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Vi udlejer mindre entreprenørmaskiner som minigravemaskiner og cementblandere til blandt andet have- og anlægsprojekter. Du får driftssikkert udstyr, der gør det manuelle arbejde nemmere.
                  </p>
                </div>
                <Link
                  to="/udlejning-af-maskiner"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Se maskinudlejning</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Card 6: Arbejde med gummiged */}
              <div className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 bg-brand-red/10 text-brand-red flex items-center justify-center text-xl mb-6">
                    <i className="fa-solid fa-tractor" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">
                    Effektivt arbejde med gummiged
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    Har du brug for hjælp til flytning af store materialemængder, planering eller udgravning? Vi udfører professionelt arbejde med gummiged til både private, erhverv og landbrug i Skals og omegn.
                  </p>
                </div>
                <Link
                  to="/arbejde-med-gummiged"
                  className="inline-flex items-center text-sm font-bold text-brand-red hover:text-brand-red-hover group"
                >
                  <span>Læs om gummiged</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Local Carrier Experience Section */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-brand-dark text-white p-8 sm:p-12 lg:p-16 relative overflow-hidden">
              {/* Background Accent Lines */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-brand-red/10 rounded-full blur-3xl pointer-events-none"></div>

              <div className="relative z-10 max-w-4xl">
                <span className="inline-block bg-brand-red text-white text-xs font-bold uppercase tracking-widest px-3 py-1 mb-6">
                  Siden 1989 • Medlem af ITD
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-6">
                  Din lokale vognmand i Skals siden 1989
                </h2>
                <div className="space-y-4 text-white/90 text-base sm:text-lg leading-relaxed mb-8">
                  <p>
                    Skals Vognmandsforretning har været etableret siden 1989 og er medlem af ITD. Vores vognmandsservice bygger på klare aftaler om opgaven, prisen, leveringsstedet og tidspunktet. Vi rådgiver i øjenhøjde og finder en løsning, som passer til den konkrete opgave for private, landbrug og virksomheder.
                  </p>
                  <p>
                    Fra vores base i Skals dækker vi især Viborg, Skive og Hobro samt opgaver i Midtjylland og Nordjylland. Vi kører også længere i Jylland og resten af Danmark efter aftale. Uanset hvor opgaven ligger, begynder et godt forløb med en grundig afklaring af transporten og de praktiske forhold.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-white/10 text-sm">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-red text-lg" aria-hidden="true"></i>
                    <span>Klare aftaler & priser</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-red text-lg" aria-hidden="true"></i>
                    <span>Rådgivning i øjenhøjde</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-red text-lg" aria-hidden="true"></i>
                    <span>Hele Jylland & Danmark</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner Section */}
        <section className="py-16 bg-neutral-900 text-white border-t border-neutral-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4">
              Få en løsning der passer til opgaven
            </h2>
            <p className="text-white/80 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed mb-8">
              Fortæl os, hvad du skal have flyttet, leveret, løftet eller kørt væk. Så hjælper vi med at afklare den rette vognmandskørsel, containerløsning eller kranopgave. Kontakt Skals Vognmandsforretning for personlig rådgivning og et uforpligtende tilbud fra en erfaren vognmand i Skals.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase tracking-wider transition-colors duration-200"
              >
                Kontakt os for tilbud
              </Link>
              <a
                href="tel:40458650"
                className="px-8 py-4 bg-white hover:bg-slate-100 text-brand-dark text-sm font-bold uppercase tracking-wider transition-colors duration-200 inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-phone text-brand-red" aria-hidden="true"></i>
                <span>Ring 40 45 86 50</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Render Contact Page (kontakt)
  if (pageSlug === 'kontakt') {
    return (
      <div className="animate-in fade-in duration-300">
        {/* Header Banner */}
        <section className="relative bg-brand-dark text-white py-16 sm:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/80 to-brand-dark/95 z-10" />
          <img
            src={HEADER_IMAGES['kontakt']}
            alt="Kontakt Skals Vognmandsforretning"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
          />
          <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-white">
              {page.header_title}
            </h1>
            <p className="text-white/90 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              {page.header_subtitle}
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Contact Info (Left Column) */}
              <div className="lg:col-span-5 space-y-8">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-text-primary tracking-tight mb-4">
                    Vores informationer
                  </h2>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    Du er altid velkommen til at skrive eller ringe til os ved at bruge nedenstående informationer.
                  </p>
                </div>

                <div className="bg-neutral-surface-light border border-border-light rounded-none p-6 sm:p-8 space-y-6">
                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="bg-brand-red/10 text-brand-red p-3 rounded-none shrink-0">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-text-primary text-xs uppercase tracking-wider mb-1">Adresse</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        <strong>Skals Vognmandsforretning</strong><br />
                        Industriparken 5<br />
                        8832 Skals
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="bg-brand-red/10 text-brand-red p-3 rounded-none shrink-0">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-text-primary text-xs uppercase tracking-wider mb-1">Telefon</h4>
                      <a href="tel:40458650" className="text-text-secondary hover:text-brand-red font-semibold text-sm transition-colors whitespace-nowrap">
                        (+45) 40 45 86 50
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="bg-brand-red/10 text-brand-red p-3 rounded-none shrink-0">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-text-primary text-xs uppercase tracking-wider mb-1">E-mail</h4>
                      <a href="mailto:evskals@gmail.com" className="text-text-secondary hover:text-brand-red font-semibold text-sm transition-colors whitespace-nowrap">
                        evskals@gmail.com
                      </a>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="pt-4 border-t border-border-light flex justify-between text-xs text-text-muted">
                    <span>CVR nr.: 13530092</span>
                    <span>Etableret: 1. oktober 1989</span>
                  </div>
                </div>
              </div>

              {/* Contact Form (Right Column) */}
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-white border-t border-border-light">
          <div className="group relative w-full h-[320px] max-h-[400px] overflow-hidden sm:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11490.495125688843!2d9.387317213951075!3d56.563743923146795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464be201ae582153%3A0xfbb0fd0e4a9ac701!2sSkals%20Vognmandsforretning%20v%2F%20Esben%20Villumsen!5e1!3m2!1sda!2sdk!4v1779972721862!5m2!1sda!2sdk"
              className="h-full w-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Skals Vognmandsforretning på kort"
            />
            <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />
          </div>
        </section>
      </div>
    );
  }

  // Render All Other Pages
  return (
    <div className="animate-in fade-in duration-300">
      {/* Header Banner */}
      <section className="relative bg-brand-dark text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/25 to-brand-dark/45 z-10" />
        <img
          src={HEADER_IMAGES[pageSlug] || HEADER_IMAGES['index']}
          alt={`${page.header_title || page.title} - Skals Vognmandsforretning`}
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-white">
            {page.header_title}
          </h1>
          {page.header_subtitle && (
            <p className="text-white/90 text-sm sm:text-base max-w-3xl mx-auto leading-relaxed">
              {page.header_subtitle}
            </p>
          )}
        </div>
      </section>

      {pageSlug === 'vores-lastbiler' && (
        <Gallery pageSlug={pageSlug} title={pageSlug === 'vores-lastbiler' ? 'Se vores lastbiler' : 'Se vores arbejde'} />
      )}

      {/* Main Content Area */}
      {pageSlug !== 'vores-lastbiler' && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Rich Text Content (Left Column) */}
              <div className="lg:col-span-8">
                <div
                  className="max-w-none text-text-secondary text-base leading-8
                    [&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-3xl [&_h1]:sm:text-4xl [&_h1]:font-extrabold [&_h1]:tracking-tight [&_h1]:text-text-primary [&_h1]:border-l-4 [&_h1]:border-brand-red [&_h1]:pl-4 [&_h1]:py-2 [&_h1]:bg-slate-50 [&_h1]:rounded-r-md
                    [&_h2]:mt-10 [&_h2]:mb-5 [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-extrabold [&_h2]:tracking-tight [&_h2]:text-text-primary [&_h2]:border-l-4 [&_h2]:border-brand-red [&_h2]:pl-3.5 [&_h2]:py-0.5
                    [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:tracking-tight [&_h3]:text-text-primary [&_h3]:border-b [&_h3]:border-slate-200 [&_h3]:pb-2.5
                    [&_h4]:mt-6 [&_h4]:mb-3 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:tracking-tight [&_h4]:text-text-primary
                    [&_h5]:mt-6 [&_h5]:mb-2 [&_h5]:text-base [&_h5]:font-bold [&_h5]:tracking-tight [&_h5]:text-text-primary
                    [&_h6]:mt-6 [&_h6]:mb-2 [&_h6]:text-sm [&_h6]:font-bold [&_h6]:tracking-tight [&_h6]:text-text-primary
                    [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:text-text-secondary
                    [&_ul]:my-6 [&_ul]:bg-slate-50/90 [&_ul]:border [&_ul]:border-slate-200 [&_ul]:p-5 [&_ul]:sm:p-6 [&_ul]:rounded-lg [&_ul]:shadow-xs [&_ul]:space-y-3 [&_ul]:list-none [&_ul]:pl-0
                    [&_ol]:my-6 [&_ol]:bg-slate-50/90 [&_ol]:border [&_ol]:border-slate-200 [&_ol]:p-5 [&_ol]:sm:p-6 [&_ol]:rounded-lg [&_ol]:shadow-xs [&_ol]:space-y-3 [&_ol]:list-decimal [&_ol]:pl-8
                    [&_li]:leading-relaxed [&_li]:text-text-primary [&_li]:text-sm [&_li]:sm:text-base [&_li]:font-medium [&_ul_li]:flex [&_ul_li]:items-start [&_ul_li]:gap-3 [&_ul_li]:before:content-['✓'] [&_ul_li]:before:text-brand-red [&_ul_li]:before:font-bold [&_ul_li]:before:shrink-0 [&_ul_li]:before:w-5 [&_ul_li]:before:h-5 [&_ul_li]:before:bg-brand-red/10 [&_ul_li]:before:rounded-full [&_ul_li]:before:flex [&_ul_li]:before:items-center [&_ul_li]:before:justify-center [&_ul_li]:before:text-xs [&_ul_li]:before:mt-0.5
                    [&_strong]:font-bold [&_strong]:text-text-primary
                    [&_a]:font-semibold [&_a]:text-brand-red [&_a]:underline [&_a]:underline-offset-4 [&_a]:transition-colors hover:[&_a]:text-brand-red-hover
                    [&_a.btn-red]:no-underline [&_a.btn-red]:bg-brand-red [&_a.btn-red]:!text-white [&_a.btn-red]:px-6 [&_a.btn-red]:py-3.5 [&_a.btn-red]:rounded-md [&_a.btn-red]:inline-flex [&_a.btn-red]:items-center [&_a.btn-red]:justify-center [&_a.btn-red]:gap-2 [&_a.btn-red]:shadow-sm hover:[&_a.btn-red]:bg-brand-red-hover hover:[&_a.btn-red]:!text-white [&_a.btn-red]:font-bold [&_a.btn-red]:text-sm
                    [&_a.btn-outline]:no-underline [&_a.btn-outline]:border-2 [&_a.btn-outline]:border-white/30 [&_a.btn-outline]:bg-white/10 [&_a.btn-outline]:!text-white [&_a.btn-outline]:px-6 [&_a.btn-outline]:py-3.5 [&_a.btn-outline]:rounded-md [&_a.btn-outline]:inline-flex [&_a.btn-outline]:items-center [&_a.btn-outline]:justify-center [&_a.btn-outline]:gap-2 hover:[&_a.btn-outline]:bg-white/20 hover:[&_a.btn-outline]:!text-white [&_a.btn-outline]:font-bold [&_a.btn-outline]:text-sm
                    [&_a.btn-dark]:no-underline [&_a.btn-dark]:bg-brand-dark [&_a.btn-dark]:!text-white [&_a.btn-dark]:px-6 [&_a.btn-dark]:py-3.5 [&_a.btn-dark]:rounded-md [&_a.btn-dark]:inline-flex [&_a.btn-dark]:items-center [&_a.btn-dark]:justify-center [&_a.btn-dark]:gap-2 hover:[&_a.btn-dark]:bg-slate-800 hover:[&_a.btn-dark]:!text-white [&_a.btn-dark]:font-bold [&_a.btn-dark]:text-sm"
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />
              </div>

              {/* Sidebar (Right Column) */}
              {pageSlug !== 'vores-lastbiler' && (
                <div className="lg:col-span-4 space-y-6">
                  {/* Quick Contact Card */}
                  <div className="bg-brand-dark text-white rounded-none p-6 sm:p-8 shadow-md border border-slate-800">
                    <h3 className="font-bold text-lg tracking-tight mb-4 text-white">
                      Vil du have et tilbud?
                    </h3>
                    <p className="text-white/80 text-xs leading-relaxed mb-6">
                      Som din vognmand er vi altid tilgængelige, hvis du har en opgave til os, og vi giver gerne et uforpligtende tilbud.
                    </p>
                    <form onSubmit={handleCallbackSubmit} className="space-y-4">
                      <input
                        name="name"
                        type="text"
                        placeholder="Navn"
                        className="w-full border border-border-light bg-white px-4 py-3 text-sm text-text-primary placeholder:text-text-muted outline-none transition-colors focus:border-brand-red"
                        required
                      />
                      <input
                        name="telephone"
                        type="tel"
                        placeholder="Telephone"
                        className="w-full border border-border-light bg-white px-4 py-3 text-sm text-text-primary placeholder:text-text-muted outline-none transition-colors focus:border-brand-red"
                        required
                      />
                      <input
                        name="website_url"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        className="sr-only absolute"
                        aria-hidden="true"
                      />
                      <button
                        type="submit"
                        className="w-full bg-brand-red hover:bg-brand-red-hover text-white font-bold py-3.5 rounded-none shadow-sm transition-colors text-xs uppercase tracking-wider"
                      >
                        Ring mig op
                      </button>
                    </form>
                  </div>

                  {/* Quick Links Card */}
                  <div className="bg-neutral-surface-light border border-border-light rounded-none p-6 sm:p-8">
                    <h3 className="font-bold text-text-primary text-xs uppercase tracking-wider mb-4">
                      Vores ydelser
                    </h3>
                    <ul className="space-y-3 text-xs">
                      {[
                        { label: 'Vognmandskørsel', href: '/vognmand' },
                        { label: 'Sand, grus og sten', href: '/grus-og-sten' },
                        { label: 'Containerkørsel', href: '/containerkoersel' },
                        { label: 'Krankørsel', href: '/krankoersel' },
                        { label: 'Udlejning af små maskiner', href: '/udlejning-af-maskiner' },
                        { label: 'Arbejde med gummiged', href: '/arbejde-med-gummiged' },
                        { label: 'Haveflis', href: '/haveflis' }
                      ].map((link, idx) => (
                        <li key={idx}>
                          <Link
                            to={link.href}
                            className={`flex items-center justify-between py-1.5 border-b border-border-light hover:text-brand-red transition-colors ${
                              location.pathname === link.href ? 'text-brand-red font-bold' : 'text-text-secondary'
                            }`}
                          >
                            <span>{link.label}</span>
                            <ChevronRight className="h-4 w-4 shrink-0" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {pageSlug !== 'vores-lastbiler' && <Gallery pageSlug={pageSlug} title="Se vores arbejde" />}
    </div>
  );
}
